export type SheetLayout = {
  pageWidthMm: number
  pageHeightMm: number
  labelWidthMm: number
  labelHeightMm: number
  marginTopBottomMm: number
  marginLeftRightMm: number
  columnGapMm: number
  rowGapMm: number
}

export enum ExportFormat {
  SINGLE = "SINGLE",
  AVERY_5392 = "AVERY_5392",
  AVERY_5390 = "AVERY_5390",
  AVERY_8395 = "AVERY_8395",
  CUSTOM_SHEET = "CUSTOM_SHEET",
}

// Avery 5392 — 4" x 3" labels, 2 columns x 3 rows on US Letter
export const AVERY_5392_SHEET: SheetLayout = {
  pageWidthMm: 215.9, // US Letter
  pageHeightMm: 279.4,
  labelWidthMm: 101.6, // 4"
  labelHeightMm: 76.2, // 3"
  marginTopBottomMm: 25.4, // 1"
  marginLeftRightMm: 6.35, // 0.25"
  columnGapMm: 0,
  rowGapMm: 0,
}

// Avery 5390 — 3.5" x 2.25" insert labels, 2 columns x 4 rows on US Letter
export const AVERY_5390_SHEET: SheetLayout = {
  pageWidthMm: 215.9,
  pageHeightMm: 279.4,
  labelWidthMm: 88.9, // 3.5"
  labelHeightMm: 57.15, // 2.25"
  marginTopBottomMm: 25.4,
  marginLeftRightMm: 19.05,
  columnGapMm: 0,
  rowGapMm: 0,
}

// Avery 8395 — 3.375" x 2.333" labels, 2 columns x 4 rows on US Letter
export const AVERY_8395_SHEET: SheetLayout = {
  pageWidthMm: 215.9, // US Letter
  pageHeightMm: 279.4,
  labelWidthMm: 85.725, // 3.375"
  labelHeightMm: 59.267, // 2.333"
  marginTopBottomMm: 14.82,
  marginLeftRightMm: 17.46,
  columnGapMm: 9.525,
  rowGapMm: 4.233,
}

export const calculateSheetGrid = (sheet: SheetLayout): { columns: number; rows: number } => {
  if (sheet.labelWidthMm <= 0 || sheet.labelHeightMm <= 0
    || sheet.pageWidthMm <= 0 || sheet.pageHeightMm <= 0) {
    return { columns: 0, rows: 0 }
  }

  const usableWidth = sheet.pageWidthMm - 2 * sheet.marginLeftRightMm
  const usableHeight = sheet.pageHeightMm - 2 * sheet.marginTopBottomMm

  const rawCols = (usableWidth + sheet.columnGapMm) / (sheet.labelWidthMm + sheet.columnGapMm)
  const rawRows = (usableHeight + sheet.rowGapMm) / (sheet.labelHeightMm + sheet.rowGapMm)

  // Round to avoid floating point errors (e.g. 2.9999999997 → 3)
  const EPSILON = 0.001
  const columns = Math.max(0, Math.floor(rawCols + EPSILON))
  const rows = Math.max(0, Math.floor(rawRows + EPSILON))

  return { columns, rows }
}

export const getSheetForFormat = (format: ExportFormat): SheetLayout | null => {
  switch (format) {
    case ExportFormat.AVERY_5392:
      return AVERY_5392_SHEET
    case ExportFormat.AVERY_5390:
      return AVERY_5390_SHEET
    case ExportFormat.AVERY_8395:
      return AVERY_8395_SHEET
    default:
      return null
  }
}

// Contain-fit algorithm: scale a badge to fit within a label cell,
// trying both orientations and picking the better fit.
export type ContainFitResult = {
  scale: number
  rotated: boolean
}

export const computeContainFit = (
  badgeWidthMm: number,
  badgeHeightMm: number,
  labelWidthMm: number,
  labelHeightMm: number,
): ContainFitResult => {
  const scaleNormal = Math.min(
    labelWidthMm / badgeWidthMm,
    labelHeightMm / badgeHeightMm,
  )

  const scaleRotated = Math.min(
    labelWidthMm / badgeHeightMm,
    labelHeightMm / badgeWidthMm,
  )

  if (scaleRotated > scaleNormal) {
    return { scale: scaleRotated, rotated: true }
  }

  return { scale: scaleNormal, rotated: false }
}

// Check whether badge dimensions match label dimensions (within tolerance),
// accounting for rotation.
export const dimensionsMatch = (
  badgeWidthMm: number,
  badgeHeightMm: number,
  labelWidthMm: number,
  labelHeightMm: number,
  toleranceMm = 0.5,
): boolean => {
  const normalMatch = Math.abs(badgeWidthMm - labelWidthMm) <= toleranceMm
    && Math.abs(badgeHeightMm - labelHeightMm) <= toleranceMm

  const rotatedMatch = Math.abs(badgeWidthMm - labelHeightMm) <= toleranceMm
    && Math.abs(badgeHeightMm - labelWidthMm) <= toleranceMm

  return normalMatch || rotatedMatch
}

export type FitStatus = "perfect" | "scales"

export type AveryCompatibleTemplate = {
  number: string
  description: string
}

export type AveryFormatCategory = {
  displayName: string
  formatLabel: string
  labelSize: string
  perSheet: number
  compatibleTemplates: AveryCompatibleTemplate[]
}

export type AveryExportFormat =
  ExportFormat.AVERY_5392 | ExportFormat.AVERY_5390 | ExportFormat.AVERY_8395

export const AVERY_FORMAT_CATEGORIES: Record<AveryExportFormat, AveryFormatCategory> = {
  [ExportFormat.AVERY_5392]: {
    displayName: "Pre-Perforated Badges",
    formatLabel: "Large Format",
    labelSize: "3\" x 4\"",
    perSheet: 6,

    compatibleTemplates: [
      { number: "5384", description: "Clip Style Kit" },
      { number: "5392", description: "Insert Refills" },
      { number: "5393", description: "Cord Style Holders" },
      { number: "74459", description: "Hanging Name Badges" },
      { number: "74536", description: "Clip Style Badges" },
      { number: "74540", description: "Pin Style Badges" },
      { number: "74541", description: "Hanging Style Badges" },
      { number: "78617", description: "EcoFriendly Clip Style" },
      { number: "78619", description: "EcoFriendly Hanging Style" },
      { number: "8780", description: "Magnetic Style Badges" },
      { number: "8781", description: "Magnetic Style Badges" },
    ],
  },
  [ExportFormat.AVERY_5390]: {
    displayName: "Pre-Perforated Badges",
    formatLabel: "Medium Format",
    labelSize: "2-1/4\" x 3-1/2\"",
    perSheet: 8,

    compatibleTemplates: [
      { number: "5383", description: "Name Tag Inserts" },
      { number: "5390", description: "Name Badge Inserts" },
      { number: "74461", description: "Clip Style Badges" },
      { number: "74549", description: "Pin Style Badges" },
    ],
  },
  [ExportFormat.AVERY_8395]: {
    displayName: "Adhesive Badges",
    formatLabel: "Standard Format",
    labelSize: "2-1/3\" x 3-3/8\"",
    perSheet: 8,

    compatibleTemplates: [
      { number: "25395", description: "Flexible White Badges" },
      { number: "42395", description: "EcoFriendly White Badges" },
      { number: "45395", description: "EcoFriendly White Badges" },
      { number: "48395", description: "EcoFriendly White Badges" },
      { number: "5395", description: "Adhesive White Badges" },
      { number: "8395", description: "Flexible White Badges" },
      { number: "85395", description: "Adhesive White Badges" },
      { number: "88395", description: "Adhesive White Badges" },
    ],
  },
}

export const getAveryCategory = (
  format: ExportFormat,
): AveryFormatCategory | undefined => AVERY_FORMAT_CATEGORIES[format as AveryExportFormat]

export const formatCategoryTitle = (cat: AveryFormatCategory): string => `${cat.displayName} \u2022 ${cat.formatLabel}`

export const formatCategorySubtitle = (cat: AveryFormatCategory): string => `${cat.labelSize}, ${cat.perSheet} per sheet`

export type SheetFormatOption = {
  format: ExportFormat
  title: string
  description: string
}

export const SHEET_FORMAT_OPTIONS: SheetFormatOption[] = [
  {
    format: ExportFormat.SINGLE,
    title: "Single Badge",
    description: "One badge per PDF page",
  },
  {
    format: ExportFormat.AVERY_5392,
    title: formatCategoryTitle(AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5392]),
    description: formatCategorySubtitle(AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5392]),
  },
  {
    format: ExportFormat.AVERY_5390,
    title: formatCategoryTitle(AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5390]),
    description: formatCategorySubtitle(AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5390]),
  },
  {
    format: ExportFormat.AVERY_8395,
    title: formatCategoryTitle(AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_8395]),
    description: formatCategorySubtitle(AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_8395]),
  },
  {
    format: ExportFormat.CUSTOM_SHEET,
    title: "Custom Sheet",
    description: "Configure your own sheet layout",
  },
]
